import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { UserOrg } from "~/lib/drizzle/type";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";

export const useGetOrganisation = (): UseQueryResult<UserOrg> => {
    const { getToken } = useAuth();

    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<UserOrg>({
            url: `${apiBaseUrl}/organisations/current`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getOrganisation'],
        queryFn,
        staleTime: 5_000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    })
}

type UpdateOrganisationPayload = {
    organisation?: Partial<UserOrg>;
    config?: {
        base_unit?: 'CATALOG_BASE_UNIT' | 'ORDER_GUIDE_UNIT';
        enable_uom_conversion?: boolean;
        unprocessed_order_alert_duration?: number;
        erp_unit?: 'SELLER_UNIT' | 'BUYER_UNIT';
        unit_matching_sequence: string[];
    }
}

export const useUpdateOrganisation = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const cleanFileUrl = (url?: string | null): string | null | undefined => {
        if (!url) return url;

        // If the url is a s3 url, we need to clean it up
        // Example: https://sayzolo-private-dev.s3.ap-southeast-1.amazonaws.com//org_7612d895-1fe2-43ae-ad71-e3a2cb246909/company/logo/4dabdbf2-411e-4fbb-bfc7-484e16ca9134.jpg?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=AKIAZQ3DRK6NJA4CYAV6%2F20241126%2Fap-southeast-1%2Fs3%2Faws4_request&X-Amz-Date=20241126T011723Z&X-Amz-Expires=3600&X-Amz-Signature=5d7ac004beef449bb018ebbe2d02134421246ec6bb66a8ca95bfd9b0acf7913d&X-Amz-SignedHeaders=host&x-id=GetObject
        // We are only interested in /org_7612d895-1fe2-43ae-ad71-e3a2cb246909/company/logo/4dabdbf2-411e-4fbb-bfc7-484e16ca9134.jpg
        const urlParts = url.split('s3.ap-southeast-1.amazonaws.com/');
        if (urlParts.length <= 1) return url;

        const filePath = urlParts[1].split('?')[0];
        return filePath;
    }

    const mutationFn = async (payload: UpdateOrganisationPayload) => {
        const token = await getToken();

        const cleanedPayload = {
            ...payload,
            organisation: {
                ...payload.organisation,
                logo: cleanFileUrl(payload.organisation?.logo),
                qrImage: cleanFileUrl(payload.organisation?.qrImage),
            }
        }

        const result = await fetchWrapper<UserOrg>({
            url: `${apiBaseUrl}/organisations/current`,
            method: 'PATCH',
            body: cleanedPayload,
            token,
        })

        return result
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: ['getOrganisation']
            });
            await queryClient.invalidateQueries({
                queryKey: ['userProfile']
            })

            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}
